import { useState, useEffect } from "react";
import Terms from "./Terms";
import MyLeagues from "./MyLeagues";
import Eliminator from "./Eliminator";
import H2HLeague from "./H2HLeague";
import Sportsbook from "./sportsbook";
import SuperAdmin from "./SuperAdmin";
import LeagueAdmin from "./LeagueAdmin";
import LeagueRulesModal, { LEAGUE_RULES } from "./LeagueRulesModal";

const API = "https://scraps-backend.onrender.com";
const FONT = `@import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;500;600;700;800&family=Barlow:wght@300;400;500;600&family=Inter:wght@400;500;600;700&display=swap');`;


const ALL_SPORTS = [
  { key:"americanfootball_nfl",      label:"NFL",               emoji:"🏈", group:"American Football" },
  { key:"americanfootball_ncaaf",    label:"College Football",  emoji:"🏈", group:"American Football" },
  { key:"basketball_nba",            label:"NBA",               emoji:"🏀", group:"Basketball" },
  { key:"basketball_ncaab",          label:"College Basketball",emoji:"🏀", group:"Basketball" },
  { key:"basketball_wnba",           label:"WNBA",              emoji:"🏀", group:"Basketball" },
  { key:"baseball_mlb",              label:"MLB",               emoji:"⚾", group:"Baseball" },
  { key:"icehockey_nhl",             label:"NHL",               emoji:"🏒", group:"Hockey" },
  { key:"soccer_epl",                label:"EPL",               emoji:"⚽", group:"Soccer" },
  { key:"soccer_uefa_champs_league", label:"Champions League",  emoji:"⚽", group:"Soccer" },
  { key:"soccer_usa_mls",            label:"MLS",               emoji:"⚽", group:"Soccer" },
  { key:"soccer_germany_bundesliga", label:"Bundesliga",        emoji:"⚽", group:"Soccer" },
  { key:"soccer_spain_la_liga",      label:"La Liga",           emoji:"⚽", group:"Soccer" },
  { key:"soccer_italy_serie_a",      label:"Serie A",           emoji:"⚽", group:"Soccer" },
  { key:"soccer_france_ligue_one",   label:"Ligue 1",           emoji:"⚽", group:"Soccer" },
  { key:"tennis_atp_french_open",    label:"Tennis",            emoji:"🎾", group:"Other" },
  { key:"mma_mixed_martial_arts",    label:"MMA / UFC",         emoji:"🥊", group:"Other" },
  { key:"golf_pga_tour",             label:"Golf (PGA)",        emoji:"⛳", group:"Other" },
  { key:"motorsport_formula_1",      label:"NASCAR / F1",       emoji:"🏎", group:"Other" },
];
const SPORT_GROUPS = [...new Set(ALL_SPORTS.map(s => s.group))];

const S = {
  wrap: { minHeight:"100vh", background:"#0d1b2a", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:24, fontFamily:"'Inter', sans-serif" },
  card: { width:"100%", maxWidth:420, background:"#ffffff", borderRadius:20, padding:32 },
  input: { width:"100%", padding:"10px 14px", background:"#f9fafb", border:"1px solid #e5e7eb", borderRadius:8, color:"#1a1a2e", fontSize:14, marginBottom:12, outline:"none", fontFamily:"'Inter', sans-serif", boxSizing:"border-box", display:"block" },
  label: { fontSize:11, fontWeight:600, color:"#6b7280", marginBottom:5, display:"block", fontFamily:"'Inter', sans-serif", letterSpacing:"0.06em" },
  btnGreen: { width:"100%", padding:12, background:"#3d9e7a", color:"#fff", border:"none", borderRadius:8, fontFamily:"'Inter', sans-serif", fontSize:14, fontWeight:600, cursor:"pointer", marginBottom:10 },
  btnGhost: { width:"100%", padding:10, background:"transparent", color:"#6b7280", border:"1px solid #e5e7eb", borderRadius:8, cursor:"pointer", fontSize:13, fontFamily:"'Inter', sans-serif" },
  err: { background:"#fef2f2", border:"1px solid #fecaca", borderRadius:8, padding:"10px 14px", fontSize:13, color:"#dc2626", marginBottom:16 },
};

function Logo({ showDescription = false }) {
  return <div style={{textAlign:"center", marginBottom:28}}>
    <div style={{ background:"#ffffff", borderRadius:12, padding:"8px 20px", display:"inline-block", marginBottom:showDescription?16:0 }}>
      <img src="/scraps_logo.png" alt="Scraps" style={{ height:70, objectFit:"contain", display:"block" }}/>
    </div>
    
    {showDescription && (
      <div style={{marginTop:18, maxWidth:380, margin:"16px auto 0", padding:"14px 18px", background:"rgba(0,229,160,0.07)", border:"1px solid rgba(0,229,160,0.2)", borderRadius:10, textAlign:"center"}}>
        <div style={{fontSize:15, color:"#f0f4ff", lineHeight:1.7}}>Think you know sports? <strong style={{color:"#00e5a0"}}>Prove it.</strong></div>
        <div style={{fontSize:13, color:"#8a96b0", marginTop:6, lineHeight:1.7}}>Bet against your crew using real Vegas lines.<br/>No real money — just credits and bragging rights.</div>
      </div>
    )}
  </div>;
}

function WelcomeScreen({ session, onContinue }) {
  return <div style={S.wrap}>
    <style>{FONT}</style>
    <div style={{...S.card, textAlign:"center", boxShadow:"0 8px 40px rgba(0,0,0,0.3)"}}>
      <div style={{fontSize:52, marginBottom:16}}>🎉</div>
      <div style={{fontFamily:"'Barlow Condensed', sans-serif", fontSize:28, fontWeight:800, color:"#3d9e7a", marginBottom:6}}>WELCOME TO SCRAPS</div>
      <div style={{fontSize:14, color:"#6b7280", marginBottom:24, lineHeight:1.8}}>
        You've joined <strong style={{color:"#1a1a2e"}}>{session.league.name}</strong>.<br/>
        Your team <strong style={{color:"#1a1a2e"}}>{session.team.team_name}</strong> is ready.
      </div>
      <div style={{background:"#f0fdf8", border:"1px solid #a7f3d0", borderRadius:12, padding:"20px 24px", marginBottom:24}}>
        <div style={{fontSize:11, color:"#6b7280", fontFamily:"'Inter', sans-serif", letterSpacing:"0.06em", marginBottom:6, fontWeight:600}}>STARTING BALANCE</div>
        <div style={{fontFamily:"'Barlow Condensed', sans-serif", fontSize:48, fontWeight:800, color:"#3d9e7a"}}>{Number(session.team.credits).toLocaleString()}</div>
        <div style={{fontSize:12, color:"#6b7280", marginTop:4}}>credits to bet with this season</div>
      </div>
      <div style={{background:"#fffbeb",border:"1px solid #fcd34d",borderRadius:8,padding:"10px 16px",marginBottom:16,fontSize:13,color:"#92400e",lineHeight:1.6}}>
        📧 Check your email to verify your account. You can still use Scraps in the meantime.
      </div>
      <button onClick={onContinue} style={S.btnGreen}>LET'S GO</button>
    </div>
  </div>;
}

function LeaguePicker({ leagues, onPick, accountName }) {
  return <div style={S.wrap}>
    <style>{FONT}</style>
    <Logo/>
    <div style={{...S.card, boxShadow:"0 8px 40px rgba(0,0,0,0.3)"}}>
      <div style={{fontFamily:"'Barlow Condensed', sans-serif", fontSize:20, fontWeight:800, marginBottom:4, color:"#1a1a2e"}}>WELCOME BACK, {accountName?.toUpperCase()}</div>
      <div style={{fontSize:13, color:"#6b7280", marginBottom:20}}>Pick a league to enter</div>
      <div style={{display:"flex", flexDirection:"column", gap:10}}>
        {leagues.map(({team, league}) => (
          <button key={team.id} onClick={() => onPick({team, league})} style={{padding:"16px 18px", background:"rgba(0,229,160,0.06)", border:"1px solid rgba(0,229,160,0.2)", borderRadius:12, cursor:"pointer", textAlign:"left"}}>
            <div style={{fontFamily:"'Barlow Condensed', sans-serif", fontSize:17, fontWeight:700, color:"#f0f4ff", marginBottom:3}}>{league.name}</div>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center"}}>
              <span style={{fontSize:13, color:"#6b7280"}}>{team.team_name}</span>
              <span style={{fontFamily:"'Barlow Condensed', sans-serif", fontSize:16, fontWeight:800, color:"#3d9e7a"}}>{Number(team.credits).toLocaleString()}</span>
            </div>
            {league.paused && <div style={{fontSize:11, color:"#ff4d6d", marginTop:4}}>⚠ League paused</div>}
          </button>
        ))}
      </div>
      <div style={{marginTop:16, paddingTop:16, borderTop:"1px solid #e5e7eb"}}>
        <div style={{fontSize:12, color:"#6b7280", marginBottom:10, textAlign:"center"}}>Join another league?</div>
        <button onClick={() => onPick("join")} style={S.btnGhost}>+ Join a new league</button>
      </div>
    </div>
  </div>;
}

function JoinForm({ onJoined, onBack, accountData }) {
  const [inviteCode, setInviteCode] = useState("");
  const [teamName, setTeamName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const join = async () => {
    if (!inviteCode || !teamName) { setError("Please fill in all fields"); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/auth/register`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ email: accountData.email, password: accountData.password, full_name: accountData.full_name, invite_code: inviteCode.toUpperCase().trim(), team_name: teamName }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      onJoined({ team: data.team, league: data.league });
    } catch(e) { setError(e.message); }
    setLoading(false);
  };

  return <div style={S.wrap}>
    <style>{FONT}</style>
    <Logo/>
    <div style={S.card}>
      <div style={{fontFamily:"'Barlow Condensed', sans-serif", fontSize:20, fontWeight:800, marginBottom:16}}>JOIN A LEAGUE</div>
      {error && <div style={S.err}>{error}</div>}
      <label style={S.label}>INVITE CODE</label>
      <input value={inviteCode} onChange={e=>setInviteCode(e.target.value)} placeholder="e.g. X7K2PQ" style={{...S.input, textTransform:"uppercase", letterSpacing:"0.12em", fontSize:20, fontFamily:"'Barlow Condensed', sans-serif", fontWeight:700}}/>
      <label style={S.label}>TEAM NAME</label>
      <input value={teamName} onChange={e=>setTeamName(e.target.value)} placeholder="Pick something creative" style={S.input}/>
      <button onClick={join} disabled={loading} style={S.btnGreen}>{loading?"JOINING...":"JOIN LEAGUE"}</button>
      <button onClick={onBack} style={S.btnGhost}>← Back</button>
    </div>
  </div>;
}

function RequestForm({ onBack, fromHub, account }) {
  const [leagueType, setLeagueType] = useState(null);
  const [showIntro, setShowIntro] = useState(false);
  const [form, setForm] = useState({ league_name:"", admin_team_name:"", admin_name: account?.full_name || account?.name || "", admin_email: account?.email || "", password:"", starting_credits:5000, max_bet:"", reason:"" });
  const isLoggedIn = !!account;
  const [selectedSports, setSelectedSports] = useState(["americanfootball_nfl","basketball_nba","baseball_mlb","icehockey_nhl"]);
  const [playerProps, setPlayerProps] = useState(false);
  const [allowMultiple, setAllowMultiple] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const toggleSport = (key) => setSelectedSports(prev => prev.includes(key) ? prev.filter(k=>k!==key) : [...prev, key]);
  const groups = [...new Set(ALL_SPORTS.map(s => s.group))];
  const currentYear = new Date().getFullYear();

  const submit = async () => {
    if (!form.league_name||!form.admin_team_name) { setError("Please fill in all required fields"); return; }
      if (!isLoggedIn && (!form.admin_name||!form.admin_email||!form.password)) { setError("Please fill in all required fields"); return; }
    setLoading(true); setError("");
    try {
      const endpoint = leagueType === "eliminator" ? `${API}/eliminator/request` : leagueType === "h2h" ? `${API}/h2h/request` : `${API}/league/request`;
      const adminName = isLoggedIn ? (account.full_name || account.name) : form.admin_name;
      const adminEmail = isLoggedIn ? account.email : form.admin_email;
      const adminPassword = isLoggedIn ? "__use_existing_account__" : form.password;
      const body = leagueType === "eliminator"
        ? { league_name:form.league_name, admin_team_name:form.admin_team_name, admin_name:adminName, admin_email:adminEmail, password:adminPassword, season_year:currentYear, start_week:1, reason:form.reason, allow_multiple_entries:allowMultiple }
        : leagueType === "h2h"
        ? { league_name:form.league_name, admin_team_name:form.admin_team_name, admin_name:adminName, admin_email:adminEmail, password:adminPassword, starting_credits:Number(form.starting_credits), sports:selectedSports, player_props:playerProps, reason:form.reason, ...h2hForm }
        : { ...form, admin_name:adminName, admin_email:adminEmail, password:adminPassword, starting_credits:Number(form.starting_credits), max_bet:form.max_bet?Number(form.max_bet):null, sports:selectedSports, player_props:playerProps, allow_multiple_entries:allowMultiple };
      const res = await fetch(endpoint, { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      setSubmitted(data.league_id);
    } catch(e) { setError(e.message); }
    setLoading(false);
  };

  const isElim = leagueType === "eliminator";
  const isH2H = leagueType === "h2h";
  const [h2hForm, setH2HForm] = useState({
    period_length_days: 7,
    period_start_day: 1,
    total_regular_periods: 10,
    playoff_teams: 4,
    playoff_rounds_per_period: 1,
  });
  const setH2H = (k,v) => setH2HForm(f=>({...f,[k]:v}));
  const accentColor = isElim ? "#dc2626" : isH2H ? "#1d4ed8" : "#3d9e7a";
  const accentBg = isElim ? "#fff5f5" : isH2H ? "#eff6ff" : "#f0fdf8";
  const accentBorder = isElim ? "#fecaca" : isH2H ? "#93c5fd" : "#a7f3d0";

  if (submitted) return <div style={S.wrap}>
    <style>{FONT}</style>
    <div style={{...S.card, textAlign:"center", maxWidth:440}}>
      <div style={{fontSize:44, marginBottom:14}}>🎉</div>
      <div style={{fontFamily:"'Barlow Condensed', sans-serif", fontSize:24, fontWeight:800, color:"#1a1a2e", marginBottom:8}}>LEAGUE REQUESTED!</div>
      <div style={{fontSize:14, color:"#6b7280", lineHeight:1.8, marginBottom:24}}>Your league is pending approval. You'll see it in your leagues tab and get an email when it's approved.</div>
      <button onClick={onBack} style={S.btnGreen}>Go to My Leagues</button>
    </div>
  </div>;

  // Step 1 — choose league type
  if (!account) return (
    <div style={{minHeight:"100vh", backgroundColor:"#0d1b2a", display:"flex", alignItems:"center", justifyContent:"center", padding:16}}>
      <div style={{background:"#fff", borderRadius:16, padding:32, textAlign:"center", maxWidth:380}}>
        <div style={{fontSize:32, marginBottom:16}}>🔒</div>
        <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontSize:22, fontWeight:800, marginBottom:8}}>SIGN IN FIRST</div>
        <div style={{fontSize:14, color:"#6b7280", marginBottom:20}}>You need an account to start a league</div>
        <button onClick={onBack} style={{width:"100%", padding:"12px", background:"#3d9e7a", color:"#fff", border:"none", borderRadius:8, fontSize:15, fontWeight:700, cursor:"pointer", fontFamily:"'Inter',sans-serif"}}>← Go Back</button>
      </div>
    </div>
  );

  if (!leagueType) return <div style={S.wrap}>
    <style>{FONT}</style>
    <Logo/>
    <div style={{...S.card, maxWidth:520}}>
      <div style={{fontFamily:"'Barlow Condensed', sans-serif", fontSize:22, fontWeight:800, marginBottom:4, color:"#1a1a2e"}}>REQUEST A LEAGUE</div>
      <div style={{fontSize:13, color:"#6b7280", marginBottom:20}}>Choose your league type — tap one to learn more.</div>
      <div style={{display:"flex", flexDirection:"column", gap:12, marginBottom:16}}>
        {[
          {
            type:"classic", emoji:"🏆", name:"CLASSIC LEAGUE", accent:"#3d9e7a", bg:"#f0fdf8", border:"#3d9e7a",
            tagline:"The ultimate season-long betting experience.",
            bullets:["Everyone starts with the same credits","Bet on any game across multiple sports","Credits accumulate all season - top the leaderboard to win","No eliminations, no resets - just pure betting skill"],
          },
          {
            type:"eliminator", emoji:"🎯", name:"NFL ELIMINATOR", accent:"#dc2626", bg:"#fff5f5", border:"#fecaca",
            tagline:"One pick a week. One wrong pick and you're out.",
            bullets:["Pick one NFL team to win each week","You can never reuse the same team","One loss and you're eliminated forever","Last person standing wins the whole thing"],
          },
          {
            type:"h2h", emoji:"⚔️", name:"HEAD TO HEAD", accent:"#1d4ed8", bg:"#eff6ff", border:"#93c5fd",
            tagline:"Go head to head against one opponent each week.",
            bullets:["Everyone resets to the same credits each matchup","Bet against your opponent's credit total","Win the week, build your record, make the playoffs","Season ends with a playoff bracket and a champion"],
          },
        ].map(lt => (
          <button key={lt.type} onClick={()=>{ setLeagueType(lt.type); setShowIntro(true); }} style={{padding:"16px 18px", background:lt.bg, border:`2px solid ${lt.border}`, borderRadius:12, cursor:"pointer", textAlign:"left", transition:"all 0.15s", width:"100%"}}>
            <div style={{display:"flex", alignItems:"center", gap:12, marginBottom:10}}>
              <span style={{fontSize:28}}>{lt.emoji}</span>
              <div>
                <div style={{fontFamily:"'Barlow Condensed', sans-serif", fontSize:18, fontWeight:800, color:"#1a1a2e"}}>{lt.name}</div>
                <div style={{fontSize:12, color:"#6b7280", marginTop:1}}>{lt.tagline}</div>
              </div>
            </div>
            <div style={{display:"flex", flexDirection:"column", gap:4}}>
              {lt.bullets.map((b,i) => (
                <div key={i} style={{display:"flex", gap:8, alignItems:"flex-start"}}>
                  <span style={{color:lt.accent, fontSize:12, marginTop:1, flexShrink:0}}>✓</span>
                  <span style={{fontSize:12, color:"#374151", lineHeight:1.5}}>{b}</span>
                </div>
              ))}
            </div>
          </button>
        ))}
      </div>
      <button onClick={onBack} style={S.btnGhost}>← Back</button>
    </div>
  </div>;

  // Intro screen - shown after picking a league type
  if (showIntro) {
    const d = LEAGUE_RULES[leagueType];
    return <div style={S.wrap}>
      <style>{FONT}</style>
      <Logo/>
      <div style={{...S.card, maxWidth:480}}>
        <button onClick={()=>{ setLeagueType(null); setShowIntro(false); }} style={{background:"none", border:"none", color:"#9ca3af", cursor:"pointer", fontSize:12, fontFamily:"'Inter', sans-serif", marginBottom:16, padding:0}}>← Back to league types</button>
        <div style={{textAlign:"center", marginBottom:20}}>
          <div style={{fontSize:48, marginBottom:8}}>{d.emoji}</div>
          <div style={{fontFamily:"'Barlow Condensed', sans-serif", fontSize:26, fontWeight:800, color:"#1a1a2e"}}>{d.name}</div>
          <div style={{fontSize:13, color:"#6b7280", marginTop:4}}>{d.subtitle}</div>
        </div>
        <div style={{background:d.bg, border:`1px solid ${d.accent}30`, borderRadius:12, padding:"14px 16px", marginBottom:20}}>
          <p style={{margin:0, fontSize:13, color:"#374151", lineHeight:1.7}}>{d.description}</p>
        </div>
        <div style={{display:"flex", flexDirection:"column", gap:12, marginBottom:24}}>
          {d.steps.map((step,i) => (
            <div key={i} style={{display:"flex", gap:12, alignItems:"flex-start"}}>
              <div style={{width:36, height:36, borderRadius:10, background:d.bg, border:`1px solid ${d.accent}30`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0}}>{step.icon}</div>
              <div>
                <div style={{fontSize:13, fontWeight:700, color:"#1a1a2e", marginBottom:2}}>{step.title}</div>
                <div style={{fontSize:12, color:"#6b7280", lineHeight:1.5}}>{step.desc}</div>
              </div>
            </div>
          ))}
        </div>
        <button onClick={()=>setShowIntro(false)} style={{width:"100%", padding:"14px", background:d.accent, color:"#fff", border:"none", borderRadius:10, fontFamily:"'Inter', sans-serif", fontSize:15, fontWeight:700, cursor:"pointer"}}>
          Let's set it up
        </button>
      </div>
    </div>;
  }

  // Step 2 — fill in details
  return <div style={{...S.wrap, justifyContent:"flex-start", paddingTop:32, paddingBottom:32}}>
    <style>{FONT}</style>
    <Logo/>
    <div style={{...S.card, maxWidth:480, position:"relative"}}>

      {/* Change type link top left */}
      <button onClick={()=>setLeagueType(null)} style={{position:"absolute", top:20, left:20, background:"none", border:"none", color:"#9ca3af", cursor:"pointer", fontSize:12, fontFamily:"'Inter', sans-serif"}}>← Change</button>

      {/* League type centered at top */}
      <div style={{textAlign:"center", marginBottom:24, paddingTop:8}}>
        <div style={{fontFamily:"'Barlow Condensed', sans-serif", fontSize:32, fontWeight:800, color:accentColor, marginBottom:2}}>
          {isElim ? "🎯 NFL ELIMINATOR" : isH2H ? "⚔️ HEAD TO HEAD" : "🏆 CLASSIC LEAGUE"}
        </div>
        <div style={{fontSize:12, color:"#6b7280"}}>{currentYear} Season</div>
      </div>

      {isElim && (
        <div style={{background:"#fffbeb", border:"1px solid #fcd34d", borderRadius:10, padding:"12px 14px", marginBottom:18}}>
          <div style={{fontSize:13, fontWeight:600, color:"#92400e", marginBottom:2}}>How it works</div>
          <div style={{fontSize:12, color:"#78350f", lineHeight:1.6}}>Pick one NFL team per week. Win = survive. Lose = eliminated. Can't reuse a team. Last person standing wins.</div>
        </div>
      )}

      {error && <div style={S.err}>{error}</div>}

      {/* League Details */}
      <div style={{fontSize:11, fontWeight:700, color:accentColor, letterSpacing:"0.08em", marginBottom:12}}>LEAGUE DETAILS</div>
      <label style={S.label}>LEAGUE NAME *</label>
      <input value={form.league_name} onChange={e=>set("league_name",e.target.value)} placeholder={isElim ? "NFL Survivor 2025" : "Friday Night League"} style={S.input}/>

      {!isElim && (
        <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:10}}>
          <div>
            <label style={S.label}>STARTING CREDITS *</label>
            <input type="number" value={form.starting_credits} onChange={e=>set("starting_credits",e.target.value)} style={S.input}/>
          </div>
          <div>
            <label style={S.label}>MAX BET</label>
            <input type="number" value={form.max_bet} onChange={e=>set("max_bet",e.target.value)} placeholder="No limit" style={S.input}/>
          </div>
        </div>
      )}

      <label style={S.label}>REASON (optional)</label>
      <input value={form.reason} onChange={e=>set("reason",e.target.value)} placeholder="Friend group, office pool..." style={S.input}/>

      {/* Divider */}
      <div style={{height:1, background:"#e5e7eb", margin:"16px 0"}}/>

      {/* Your Info - always show account since login is required */}
      <div style={{backgroundColor:"#f0fdf8", border:"1px solid #a7f3d0", borderRadius:10, padding:"12px 16px", marginBottom:16}}>
        <div style={{fontSize:11, fontWeight:700, color:accentColor, letterSpacing:"0.08em", marginBottom:6}}>CREATING AS</div>
        <div style={{fontSize:14, fontWeight:600, color:"#1a1a2e"}}>{account?.full_name || account?.name}</div>
        <div style={{fontSize:12, color:"#6b7280"}}>{account?.email}</div>
      </div>
      <label style={S.label}>YOUR TEAM NAME *</label>
      <input value={form.admin_team_name} onChange={e=>set("admin_team_name",e.target.value)} placeholder="Your team name in the league" style={S.input}/>

      {/* Sports selector - Classic only */}
      {isH2H && <>
        <div style={{height:1, background:"#e5e7eb", margin:"16px 0"}}/>
        <div style={{fontSize:11, fontWeight:700, color:accentColor, letterSpacing:"0.08em", marginBottom:12}}>MATCHUP SETTINGS</div>
        <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:12}}>
          <div>
            <label style={S.label}>MATCHUP LENGTH</label>
            <select value={h2hForm.period_length_days} onChange={e=>setH2H("period_length_days",Number(e.target.value))} style={S.input}>
              <option value={1}>1 day</option>
              <option value={3}>3 days</option>
              <option value={5}>5 days</option>
              <option value={7}>7 days (1 week)</option>
              <option value={14}>14 days (2 weeks)</option>
            </select>
          </div>
          <div>
            <label style={S.label}>REGULAR SEASON MATCHUPS</label>
            <input type="number" value={h2hForm.total_regular_periods} onChange={e=>setH2H("total_regular_periods",Number(e.target.value))} min={1} max={30} style={S.input}/>
          </div>
        </div>
        <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:12}}>
          <div>
            <label style={S.label}>PLAYOFF TEAMS (0 = no playoffs)</label>
            <input type="number" value={h2hForm.playoff_teams} onChange={e=>setH2H("playoff_teams",Number(e.target.value))} min={0} max={16} style={S.input}/>
          </div>
          {h2hForm.playoff_teams > 0 && (
            <div>
              <label style={S.label}>WEEKS PER PLAYOFF ROUND</label>
              <select value={h2hForm.playoff_rounds_per_period} onChange={e=>setH2H("playoff_rounds_per_period",Number(e.target.value))} style={S.input}>
                <option value={1}>1 week per round</option>
                <option value={2}>2 weeks per round</option>
              </select>
            </div>
          )}
        </div>
        {h2hForm.playoff_teams > 0 && (
          <div style={{background:accentBg, border:`1px solid ${accentBorder}`, borderRadius:8, padding:"10px 14px", marginBottom:12, fontSize:12, color:accentColor, fontFamily:"'Inter',sans-serif"}}>
            📅 Total length: <strong>{h2hForm.total_regular_periods + Math.ceil(Math.log2(Math.max(h2hForm.playoff_teams,2))) * h2hForm.playoff_rounds_per_period} matchups</strong> × {h2hForm.period_length_days} days = <strong>{(h2hForm.total_regular_periods + Math.ceil(Math.log2(Math.max(h2hForm.playoff_teams,2))) * h2hForm.playoff_rounds_per_period) * h2hForm.period_length_days} days total</strong>
          </div>
        )}
      </>}

      {!isElim && <>
        <div style={{height:1, background:"#e5e7eb", margin:"16px 0"}}/>
        <div style={{fontSize:11, fontWeight:700, color:accentColor, letterSpacing:"0.08em", marginBottom:12}}>SELECT SPORTS *</div>
        {groups.map(group => (
          <div key={group} style={{marginBottom:14}}>
            <div style={{fontSize:10, fontWeight:600, color:"#9ca3af", marginBottom:8, letterSpacing:"0.06em"}}>{group.toUpperCase()}</div>
            <div style={{display:"flex", flexWrap:"wrap", gap:8}}>
              {ALL_SPORTS.filter(s=>s.group===group).map(sport => {
                const selected = selectedSports.includes(sport.key);
                return (
                  <button key={sport.key} onClick={()=>toggleSport(sport.key)} style={{
                    padding:"7px 14px", borderRadius:8,
                    border:`1.5px solid ${selected ? accentColor : "#e5e7eb"}`,
                    background: selected ? accentBg : "#f9fafb",
                    color: selected ? accentColor : "#6b7280",
                    cursor:"pointer", fontSize:13, fontFamily:"'Inter', sans-serif", fontWeight: selected ? 600 : 400,
                    transition:"all 0.12s",
                  }}>
                    {sport.emoji} {sport.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        {leagueType !== "h2h" && (
          <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 14px", background:"#f9fafb", border:"1px solid #e5e7eb", borderRadius:8, marginBottom:12, opacity:0.6}}>
            <div>
              <div style={{fontSize:14, fontWeight:600, color:"#9ca3af"}}>Player Props</div>
              <div style={{fontSize:12, color:"#9ca3af"}}>Coming soon</div>
            </div>
            <div style={{width:44, height:24, borderRadius:12, background:"#e5e7eb", position:"relative", cursor:"not-allowed"}}>
              <div style={{position:"absolute", top:3, left:3, width:18, height:18, borderRadius:"50%", background:"#fff", boxShadow:"0 1px 3px rgba(0,0,0,0.2)"}}/>
            </div>
          </div>
        )}
      </>}

      {!isH2H && (
        <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 14px", background:"#f9fafb", border:"1px solid #e5e7eb", borderRadius:8, marginBottom:12}}>
          <div>
            <div style={{fontSize:14, fontWeight:600, color:"#1a1a2e"}}>Allow multiple entries</div>
            <div style={{fontSize:12, color:"#6b7280"}}>Members can join with more than one team</div>
          </div>
          <div onClick={()=>setAllowMultiple(!allowMultiple)} style={{width:44, height:24, borderRadius:12, background:allowMultiple?accentColor:"#e5e7eb", cursor:"pointer", position:"relative", transition:"background 0.2s"}}>
            <div style={{position:"absolute", top:3, left:allowMultiple?23:3, width:18, height:18, borderRadius:"50%", background:"#fff", transition:"left 0.2s", boxShadow:"0 1px 3px rgba(0,0,0,0.2)"}}/>
          </div>
        </div>
      )}

      <button onClick={submit} disabled={loading} style={{...S.btnGreen, background:accentColor, marginTop:8, color:"#ffffff"}}>
        {loading ? "Submitting..." : "Submit Request"}
      </button>
      <button onClick={()=>setLeagueType(null)} style={S.btnGhost}>← Back</button>
    </div>
  </div>;
}



// ─── MAIN LANDING ─────────────────────────────────────────────────────────────
function LeagueExpandList({ onRequest }) {
  const [expanded, setExpanded] = useState(null);

  const leagues = [
    {
      id:"classic", emoji:"🏆", name:"Classic League", tag:"Season-long credits league", accent:"#3d9e7a",
      description:"Classic is your traditional fantasy-style betting league. Everyone starts with the same amount of credits and bets on real games across your chosen sports. There are no weekly resets and no eliminations - your credits build up over the entire season. The person with the most credits at the end wins.",
      bullets:[
        { icon:"💰", text:"Everyone starts with the same credits - your bankroll for the whole season." },
        { icon:"🏈", text:"Bet on any game across NFL, CFB, MLB, NBA, NHL and more." },
        { icon:"📈", text:"Every win adds to your total. Credits carry over week after week." },
        { icon:"🏆", text:"Most credits at the end of the season wins. Simple as that." },
      ],
    },
    {
      id:"eliminator", emoji:"🎯", name:"NFL Eliminator", tag:"Last man standing", accent:"#dc2626",
      description:"NFL Eliminator is the ultimate high-stakes pick'em game. Every week you pick one NFL team to win their game. Get it right and you survive. Get it wrong and you're eliminated. The catch? You can never use the same team twice all season. One bad pick and you're done.",
      bullets:[
        { icon:"🏈", text:"Pick one NFL team each week. One pick per week." },
        { icon:"🚫", text:"Once you use a team, they're off your list forever." },
        { icon:"💀", text:"If your team loses, you're out permanently. No second chances." },
        { icon:"🏆", text:"Last survivor wins the whole thing." },
      ],
    },
    {
      id:"h2h", emoji:"⚔️", name:"Head to Head", tag:"Matchups + Playoffs", accent:"#1d4ed8",
      description:"Think Fantasy Football for gambling. Go 1-on-1 against one opponent each matchup. Both start with the same credits - bet to grow your stack and win. Best records make the playoffs.",
      bullets:[
        { icon:"⚔️", text:"Face off 1-on-1 against one opponent each week." },
        { icon:"🎰", text:"Both reset to the same credits - bet to grow your stack." },
        { icon:"📊", text:"Most credits at the end of the matchup wins the matchup." },
        { icon:"🏆", text:"Best records advance to a playoff bracket. Win the bracket, win the league." },
      ],
    },
  ];

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:10, maxWidth:560, margin:"0 auto 48px", textAlign:"left" }}>
      {leagues.map(l => (
        <div key={l.id} style={{ backgroundColor:"#ffffff", borderRadius:12, border:"1px solid #e5e7eb", overflow:"hidden", boxShadow:"0 1px 3px rgba(0,0,0,0.06)" }}>
          {/* Row */}
          <div style={{ display:"flex", alignItems:"center", gap:14, padding:"16px 18px", cursor:"pointer" }} onClick={()=>setExpanded(expanded===l.id?null:l.id)}>
            <span style={{ fontSize:22, flexShrink:0 }}>{l.emoji}</span>
            <div style={{ flex:1 }}>
              <div style={{ fontFamily:"'Inter', sans-serif", fontSize:15, fontWeight:700, color:"#0d1b2a" }}>{l.name}</div>
              <div style={{ fontSize:12, color:l.accent, marginTop:2, fontWeight:500 }}>{l.tag}</div>
            </div>
            <span style={{ fontSize:11, padding:"2px 8px", borderRadius:20, backgroundColor:`${l.accent}15`, color:l.accent, fontWeight:700, flexShrink:0, marginRight:6 }}>LIVE</span>
            <span style={{ color:"#9ca3af", fontSize:13, flexShrink:0 }}>{expanded===l.id?"▲":"▼"}</span>
          </div>
          {/* Expanded */}
          {expanded===l.id && (
            <div style={{ padding:"0 18px 18px", borderTop:"1px solid #f3f4f6" }}>
              <p style={{ fontSize:13, color:"#6b7280", lineHeight:1.7, margin:"14px 0 12px" }}>{l.description}</p>
              <div style={{ display:"flex", flexDirection:"column", gap:6, marginBottom:14 }}>
                {l.bullets.map((b,i)=>(
                  <div key={i} style={{ display:"flex", gap:10, alignItems:"flex-start" }}>
                    <span style={{ fontSize:15, flexShrink:0 }}>{b.icon}</span>
                    <span style={{ fontSize:13, color:"#374151", lineHeight:1.5 }}>{b.text}</span>
                  </div>
                ))}
              </div>
              <button onClick={onRequest} style={{ padding:"8px 18px", background:l.accent, border:"none", borderRadius:7, color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                Start this league
              </button>
            </div>
          )}
        </div>
      ))}
      {/* Coming soon row */}
      <div style={{ display:"flex", alignItems:"center", gap:14, backgroundColor:"#f9fafb", borderRadius:12, padding:"14px 18px", border:"1px dashed #e5e7eb" }}>
        <span style={{ fontSize:22, flexShrink:0 }}>✨</span>
        <div style={{ flex:1 }}>
          <div style={{ fontFamily:"'Inter', sans-serif", fontSize:15, fontWeight:600, color:"#9ca3af" }}>More coming soon</div>
          <div style={{ fontSize:12, color:"#9ca3af", marginTop:2 }}>
            Have an idea?{" "}
            <a href="mailto:hello@playscraps.com?subject=League Idea" style={{ color:"#3d9e7a", textDecoration:"none", fontWeight:600 }}>
              Email us at hello@playscraps.com
            </a>
          </div>
        </div>
        <span style={{ fontSize:11, padding:"2px 8px", borderRadius:20, backgroundColor:"#f3f4f6", color:"#9ca3af", fontWeight:700, flexShrink:0 }}>SOON</span>
      </div>
    </div>
  );
}

function HomePage({ onSignIn, onJoin, onRequest, onCreateAccount }) {
  const leagues = [
    {
      id:"classic", emoji:"🏆", name:"Classic League", accent:"#3d9e7a",
      tagline:"Season-long credits league",
      description:"Everyone starts with the same credits and bets across any sport all season long. Top the leaderboard by the end and you win.",
      bullets:["Any sport - NFL, NBA, MLB, CFB and more","Spreads, parlays, teasers","Credits accumulate all season","No eliminations - pure betting skill"],
    },
    {
      id:"eliminator", emoji:"🎯", name:"NFL Eliminator", accent:"#dc2626",
      tagline:"Last man standing",
      description:"Pick one NFL team each week. Win and you survive. Lose and you're out. Never pick the same team twice all season.",
      bullets:["One pick per week","Can't reuse a team all season","One wrong pick = eliminated","Last survivor takes it all"],
    },
    {
      id:"h2h", emoji:"⚔️", name:"Head to Head", accent:"#1d4ed8",
      tagline:"Matchups + Playoffs",
      description:"Think Fantasy Football for gambling. Go 1-on-1 against one opponent each matchup. Both start with the same credits - bet to grow your stack and win.",
      bullets:["1v1 matchups on your schedule","Everyone resets each matchup","Best records make the playoffs","Bracket ends with a champion"],
    },
  ];

  return (
    <div style={{ minHeight:"100vh", fontFamily:"'Inter', sans-serif", backgroundColor:"#f3f4f6" }}>
      <style>{FONT}</style>

      {/* Hero */}
      <div style={{ backgroundColor:"#0d1b2a", padding:"36px 24px 44px", textAlign:"center" }}>
        <div style={{ background:"#ffffff", borderRadius:16, padding:"10px 24px", marginBottom:20, display:"inline-block" }}>
          <img src="/scraps_logo.png" alt="Scraps" style={{ height:"clamp(60px, 10vw, 100px)", objectFit:"contain", display:"block" }}/>
        </div>
        <div style={{ fontSize:"clamp(14px, 2.2vw, 17px)", color:"#9ca3af", lineHeight:1.7, maxWidth:460, margin:"0 auto 24px" }}>
          The sports gambling marketplace. One place for every league you and your crew want to play. Don't see your format? Build it.
        </div>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:10, flexWrap:"wrap" }}>
          <button onClick={onCreateAccount} style={{ padding:"13px 32px", background:"#3d9e7a", border:"none", borderRadius:8, color:"#fff", fontSize:15, fontWeight:700, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
            Create Account
          </button>
          <button onClick={onSignIn} style={{ padding:"13px 28px", background:"#ffffff", border:"none", borderRadius:8, color:"#3d9e7a", fontSize:15, fontWeight:700, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
            Sign In
          </button>
        </div>
      </div>

      {/* League cards */}
      <div style={{ maxWidth:1000, margin:"0 auto", padding:"36px 20px 0" }}>
        <div style={{ fontSize:13, fontWeight:600, color:"#6b7280", letterSpacing:"0.06em", marginBottom:16, textTransform:"uppercase" }}>Available Now</div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(300px, 1fr))", gap:20, marginBottom:20 }}>
          {leagues.map(l => (
            <div key={l.id} style={{ backgroundColor:"#ffffff", borderRadius:14, padding:"28px", border:"1px solid #e5e7eb", boxShadow:"0 1px 4px rgba(0,0,0,0.06)", display:"flex", flexDirection:"column" }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
                <span style={{ fontSize:26 }}>{l.emoji}</span>
                <div>
                  <div style={{ fontFamily:"'Inter', sans-serif", fontSize:15, fontWeight:800, color:"#0d1b2a" }}>{l.name}</div>
                  <div style={{ fontSize:11, color:l.accent, fontWeight:600, marginTop:1 }}>{l.tagline}</div>
                </div>
              </div>
              <div style={{ fontSize:13, color:"#6b7280", lineHeight:1.6, marginBottom:12 }}>{l.description}</div>
              <div style={{ display:"flex", flexDirection:"column", gap:5, flex:1 }}>
                {l.bullets.map((b,i) => (
                  <div key={i} style={{ display:"flex", gap:8, alignItems:"center" }}>
                    <div style={{ width:5, height:5, borderRadius:"50%", backgroundColor:l.accent, flexShrink:0 }}/>
                    <span style={{ fontSize:12, color:"#374151" }}>{b}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}


        </div>

        {/* More coming banner */}
        <div style={{ backgroundColor:"#f9fafb", borderRadius:10, padding:"14px 24px", border:"1px dashed #d1d5db", marginBottom:20, display:"flex", alignItems:"center", justifyContent:"center", gap:10 }}>
          <span style={{ fontSize:18 }}>✨</span>
          <span style={{ fontSize:13, color:"#9ca3af", fontWeight:500 }}>More formats are always in the works. This marketplace keeps growing.</span>
        </div>

        {/* Single CTA */}
        <div style={{ textAlign:"center", marginBottom:20 }}>
          <button onClick={onCreateAccount} style={{ padding:"14px 44px", background:"#3d9e7a", border:"none", borderRadius:9, color:"#fff", fontSize:16, fontWeight:700, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
            Start a League
          </button>
        </div>

        {/* Submit idea */}
        <div style={{ backgroundColor:"#ffffff", borderRadius:14, padding:"28px 32px", border:"1px solid #e5e7eb", marginBottom:36, textAlign:"center" }}>
          <div style={{ fontSize:16, fontWeight:700, color:"#0d1b2a", marginBottom:8 }}>Have an idea for a league?</div>
          <div style={{ fontSize:14, color:"#6b7280", lineHeight:1.6, maxWidth:480, margin:"0 auto 20px" }}>
            Scraps is a marketplace. If you have a format you and your crew have always played, pitch it to us. If it's good we'll build it and add it here for everyone.
          </div>
          <a href="mailto:hello@playscraps.com?subject=League Idea" style={{ display:"inline-block", padding:"12px 28px", background:"#0d1b2a", border:"none", borderRadius:8, color:"#fff", fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:"'Inter',sans-serif", textDecoration:"none" }}>
            Submit an idea
          </a>
        </div>
      </div>

      {/* Footer */}
      <div style={{ backgroundColor:"#0d1b2a", padding:"24px", textAlign:"center" }}>
        <div style={{ background:"#ffffff", borderRadius:12, padding:"6px 16px", marginBottom:10, display:"inline-block" }}>
          <img src="/scraps_logo.png" alt="Scraps" style={{ height:36, objectFit:"contain", display:"block" }}/>
        </div>
        <div style={{ fontSize:12, color:"#4b5563" }}>playscraps.com · hello@playscraps.com</div>
      </div>
    </div>
  );
}

function LeagueAdminOverlay({ leagueId, leagueType, token, league, onBack }) {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!leagueId) return;
    // Clear old admin session first so LeagueAdmin doesn't restore stale data
    localStorage.removeItem("scraps_la_token");
    localStorage.removeItem("scraps_la_league");
    fetch(`${API}/auth/league-admin/login-with-account`, {
      method:"POST",
      headers:{"Content-Type":"application/json", Authorization:`Bearer ${token}`},
      body: JSON.stringify({ league_id: leagueId }),
    }).then(r=>r.json()).then(data => {
      if (data.token) {
        localStorage.setItem("scraps_la_token", data.token);
        localStorage.setItem("scraps_la_league", leagueId);
        setReady(true);
      } else {
        setError(data.detail || "Not authorized as admin");
      }
    }).catch(() => setError("Failed to load admin panel"));
  }, [leagueId]);

  if (error) return (
    <div style={{ height:"100vh", backgroundColor:"#0d1b2a", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:16 }}>
      <div style={{ color:"#dc2626", fontSize:14 }}>{error}</div>
      <button onClick={onBack} style={{ padding:"8px 20px", background:"#3d9e7a", border:"none", borderRadius:8, color:"#fff", cursor:"pointer" }}>← Go Back</button>
    </div>
  );

  if (!ready) return (
    <div style={{ height:"100vh", backgroundColor:"#0d1b2a", display:"flex", alignItems:"center", justifyContent:"center", color:"#6b7280", fontFamily:"'Inter',sans-serif" }}>
      Loading admin panel...
    </div>
  );

  return <LeagueAdmin onBack={onBack} />;
}

function SignupWall({ intent, onSignIn, onBack, onCreated }) {
  const [form, setForm] = useState({ fullName:"", email:"", password:"", teamName:"" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm(f => ({...f, [k]: v}));

  const handleCreate = async () => {
    if (!form.fullName || !form.email || !form.password) { setError("Please fill in all fields"); return; }
    if (form.password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setLoading(true);
    setError("");
    try {
      // Create account via register with a placeholder league
      // We just need to create the account — no league yet
      const res = await fetch(`${API}/auth/create-account`, {
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ full_name: form.fullName, email: form.email, password: form.password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to create account");
      onCreated(data);
    } catch(e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const intentLabel = intent === "request" ? "start a league" : intent === "join" ? "join a league" : "get started";

  return (
    <div style={{ minHeight:"100vh", backgroundColor:"#0d1b2a", display:"flex", alignItems:"center", justifyContent:"center", padding:16, fontFamily:"'Inter',sans-serif" }}>
      <style>{FONT}</style>
      <div style={{ backgroundColor:"#ffffff", borderRadius:20, padding:"32px 28px", width:"100%", maxWidth:400 }}>
        <div style={{ textAlign:"center", marginBottom:24 }}>
          <img src="/scraps_logo.png" alt="Scraps" style={{ height:90, objectFit:"contain", marginBottom:12 }}/>
          <div style={{ fontSize:16, fontWeight:700, color:"#1a1a2e", marginBottom:4 }}>Create your account</div>
          <div style={{ fontSize:13, color:"#6b7280" }}>You need an account to {intentLabel}</div>
        </div>

        {error && <div style={{ backgroundColor:"#fff5f5", border:"1px solid #fecaca", borderRadius:8, padding:"10px 14px", marginBottom:16, fontSize:13, color:"#dc2626" }}>{error}</div>}

        <div style={{ marginBottom:12 }}>
          <label style={{ fontSize:11, fontWeight:600, color:"#6b7280", letterSpacing:"0.06em", display:"block", marginBottom:5 }}>YOUR NAME</label>
          <input value={form.fullName} onChange={e=>set("fullName",e.target.value)} placeholder="Full name" style={{ width:"100%", padding:"11px 14px", border:"1.5px solid #e5e7eb", borderRadius:8, fontSize:14, fontFamily:"'Inter',sans-serif", boxSizing:"border-box", outline:"none" }}/>
        </div>
        <div style={{ marginBottom:12 }}>
          <label style={{ fontSize:11, fontWeight:600, color:"#6b7280", letterSpacing:"0.06em", display:"block", marginBottom:5 }}>EMAIL</label>
          <input type="email" value={form.email} onChange={e=>set("email",e.target.value)} placeholder="you@email.com" style={{ width:"100%", padding:"11px 14px", border:"1.5px solid #e5e7eb", borderRadius:8, fontSize:14, fontFamily:"'Inter',sans-serif", boxSizing:"border-box", outline:"none" }}/>
        </div>
        <div style={{ marginBottom:20 }}>
          <label style={{ fontSize:11, fontWeight:600, color:"#6b7280", letterSpacing:"0.06em", display:"block", marginBottom:5 }}>PASSWORD</label>
          <input type="password" value={form.password} onChange={e=>set("password",e.target.value)} placeholder="At least 6 characters" style={{ width:"100%", padding:"11px 14px", border:"1.5px solid #e5e7eb", borderRadius:8, fontSize:14, fontFamily:"'Inter',sans-serif", boxSizing:"border-box", outline:"none" }}/>
        </div>

        <button onClick={handleCreate} disabled={loading} style={{ width:"100%", padding:"13px", backgroundColor:"#3d9e7a", color:"#fff", border:"none", borderRadius:8, fontSize:15, fontWeight:700, cursor:"pointer", fontFamily:"'Inter',sans-serif", marginBottom:16 }}>
          {loading ? "Creating account..." : "Create Account"}
        </button>

        <div style={{ textAlign:"center", fontSize:13, color:"#6b7280" }}>
          Already have an account?{" "}
          <button onClick={onSignIn} style={{ background:"none", border:"none", color:"#3d9e7a", fontWeight:700, cursor:"pointer", fontSize:13, fontFamily:"'Inter',sans-serif" }}>Sign in</button>
        </div>

        <div style={{ textAlign:"center", marginTop:12 }}>
          <button onClick={onBack} style={{ background:"none", border:"none", color:"#9ca3af", cursor:"pointer", fontSize:12, fontFamily:"'Inter',sans-serif" }}>← Back to home</button>
        </div>
      </div>
    </div>
  );
}

function Landing({ onLoggedIn, onSuperAdmin, onLeagueAdmin, onJoin, onRequest, onForgot, onTerms, onHome }) {

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const login = async () => {
    if (!email || !password) { setError("Please fill in all fields"); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/auth/login`, { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({email, password}) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      onLoggedIn(data);
    } catch(e) { setError(e.message); }
    setLoading(false);
  };

  const inputStyle = {
    width:"100%", padding:"10px 14px", background:"#f9fafb", border:"1px solid #e5e7eb",
    borderRadius:8, fontFamily:"'Inter', sans-serif", fontSize:14, color:"#1a1a2e",
    marginBottom:12, outline:"none", boxSizing:"border-box", display:"block",
  };

  return (
    <div style={{ minHeight:"100vh", background:"#0d1b2a", display:"flex", alignItems:"center", justifyContent:"center", padding:"16px", fontFamily:"'Inter', sans-serif", position:"relative" }}>
      <style>{FONT}</style>
      <div style={{ background:"#ffffff", borderRadius:20, padding:"24px 28px", width:"100%", maxWidth:420 }}>
        <div style={{ textAlign:"center", marginBottom:16 }}>
          <img src="/scraps_logo.png" alt="Scraps" style={{ height:90, objectFit:"contain", marginBottom:12 }}/>
        </div>
        <div style={{ background:"#f0fdf8", border:"1px solid #a7f3d0", borderRadius:10, padding:"12px 16px", margin:"16px 0 24px", textAlign:"center" }}>
          <div style={{ fontSize:14, fontWeight:600, color:"#1a1a2e", marginBottom:4 }}>
            Think you know sports? <span style={{ color:"#3d9e7a" }}>Prove it.</span>
          </div>
          <div style={{ fontSize:12, color:"#6b7280", lineHeight:1.6 }}>
            Bet against your crew using real Vegas lines — no real money, just credits and bragging rights.
          </div>
        </div>
        {error && <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:8, padding:"10px 14px", fontSize:13, color:"#dc2626", marginBottom:16 }}>{error}</div>}
        <label style={{ fontSize:11, fontWeight:600, color:"#6b7280", letterSpacing:"0.06em", marginBottom:5, display:"block" }}>EMAIL</label>
        <input value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@email.com" style={inputStyle}/>
        <label style={{ fontSize:11, fontWeight:600, color:"#6b7280", letterSpacing:"0.06em", marginBottom:5, display:"block" }}>PASSWORD</label>
        <input type="password" value={password} onChange={e=>setPassword(e.target.value)} onKeyDown={e=>e.key==="Enter"&&login()} placeholder="Your password" style={inputStyle}/>
        <button onClick={login} disabled={loading} style={{ width:"100%", padding:"12px", background:"#3d9e7a", color:"#ffffff", border:"none", borderRadius:8, fontFamily:"'Inter', sans-serif", fontSize:14, fontWeight:600, cursor:"pointer", marginBottom:6 }}>
          {loading ? "Signing in..." : "Sign In"}
        </button>
        <div style={{ textAlign:"center", marginBottom:20 }}>
          <button onClick={onForgot} style={{ background:"none", border:"none", color:"#9ca3af", cursor:"pointer", fontSize:12 }}>Forgot password?</button>
        </div>
        <div style={{ textAlign:"center", fontSize:13, color:"#6b7280", marginBottom:8 }}>
          Don't have an account?{" "}
          <span onClick={onHome} style={{ color:"#3d9e7a", fontWeight:600, cursor:"pointer" }}>Go back to home →</span>
        </div>
        <div style={{ textAlign:"center", fontSize:11, color:"#9ca3af", marginTop:16 }}>
          By signing in you agree to our{" "}
          <span onClick={onTerms} style={{ color:"#1d4ed8", cursor:"pointer" }}>Terms of Service</span>
        </div>
      </div>
      <button onClick={onSuperAdmin} style={{ position:"fixed", bottom:16, right:16, background:"none", border:"none", cursor:"pointer", color:"rgba(255,255,255,0.12)", fontSize:18 }} title="Admin">🔒</button>
      {onHome && <button onClick={onHome} style={{ position:"fixed", bottom:16, left:16, background:"none", border:"none", cursor:"pointer", color:"rgba(255,255,255,0.3)", fontSize:12, fontFamily:"'Inter',sans-serif" }}>← Home</button>}
    </div>
  );
}

// ─── FORGOT PASSWORD ──────────────────────────────────────────────────────────
function ForgotPasswordScreen({ onBack }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (!email) { setError("Please enter your email"); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/auth/forgot-password`, { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({email}) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      setSent(true);
    } catch(e) { setError(e.message); }
    setLoading(false);
  };

  const cardStyle = { background:"#ffffff", borderRadius:20, padding:"36px 32px", width:"100%", maxWidth:420 };

  return <div style={{ minHeight:"100vh", background:"#0d1b2a", display:"flex", alignItems:"center", justifyContent:"center", padding:"32px 24px", fontFamily:"'Inter', sans-serif" }}>
    <style>{FONT}</style>
    <div style={cardStyle}>
      <div style={{ textAlign:"center", marginBottom:24 }}>
        <img src="/scraps_logo.png" alt="Scraps" style={{ height:90, objectFit:"contain", marginBottom:12 }}/>
      </div>
      {sent ? <>
        <div style={{ textAlign:"center", padding:"16px 0" }}>
          <div style={{ fontSize:40, marginBottom:12 }}>📧</div>
          <div style={{ fontFamily:"'Barlow Condensed', sans-serif", fontSize:22, fontWeight:800, color:"#3d9e7a", marginBottom:8 }}>CHECK YOUR EMAIL</div>
          <div style={{ fontSize:13, color:"#6b7280", lineHeight:1.8, marginBottom:20 }}>If an account exists for <strong style={{ color:"#1a1a2e" }}>{email}</strong> you'll receive a reset link shortly.</div>
          <button onClick={onBack} style={{ width:"100%", padding:12, background:"#111827", color:"#fff", border:"none", borderRadius:8, fontFamily:"'Inter', sans-serif", fontSize:14, fontWeight:600, cursor:"pointer" }}>Back to Login</button>
        </div>
      </> : <>
        <div style={{ fontFamily:"'Barlow Condensed', sans-serif", fontSize:22, fontWeight:800, color:"#1a1a2e", marginBottom:4 }}>FORGOT PASSWORD</div>
        <div style={{ fontSize:13, color:"#6b7280", marginBottom:20, fontFamily:"'Inter', sans-serif" }}>Enter your email and we'll send you a reset link</div>
        {error && <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:8, padding:"10px 14px", fontSize:13, color:"#dc2626", marginBottom:16 }}>{error}</div>}
        <label style={{ fontSize:11, fontWeight:600, color:"#6b7280", letterSpacing:"0.06em", marginBottom:5, display:"block", fontFamily:"'Inter', sans-serif" }}>EMAIL</label>
        <input value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={e=>e.key==="Enter"&&submit()} placeholder="you@email.com" style={{ width:"100%", padding:"10px 14px", background:"#f9fafb", border:"1px solid #e5e7eb", borderRadius:8, fontFamily:"'Inter', sans-serif", fontSize:14, color:"#1a1a2e", marginBottom:12, outline:"none" }}/>
        <button onClick={submit} disabled={loading} style={{ width:"100%", padding:12, background:"#111827", color:"#fff", border:"none", borderRadius:8, fontFamily:"'Inter', sans-serif", fontSize:14, fontWeight:600, cursor:"pointer", marginBottom:10 }}>{loading ? "Sending..." : "Send Reset Link"}</button>
        <button onClick={onBack} style={{ width:"100%", padding:10, background:"transparent", color:"#6b7280", border:"1px solid #e5e7eb", borderRadius:8, fontFamily:"'Inter', sans-serif", cursor:"pointer", fontSize:13 }}>← Back to login</button>
      </>}
    </div>
  </div>;
}

function ResetPasswordScreen({ token, onDone }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!password || !confirm) { setError("Please fill in both fields"); return; }
    if (password !== confirm) { setError("Passwords don't match"); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/auth/reset-password`, { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({token, new_password:password}) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      setDone(true);
    } catch(e) { setError(e.message); }
    setLoading(false);
  };

  const cardStyle = { background:"#ffffff", borderRadius:20, padding:"36px 32px", width:"100%", maxWidth:420 };

  return <div style={{ minHeight:"100vh", background:"#0d1b2a", display:"flex", alignItems:"center", justifyContent:"center", padding:"32px 24px", fontFamily:"'Inter', sans-serif" }}>
    <style>{FONT}</style>
    <div style={cardStyle}>
      <div style={{ textAlign:"center", marginBottom:24 }}>
        <img src="/scraps_logo.png" alt="Scraps" style={{ height:90, objectFit:"contain", marginBottom:12 }}/>
      </div>
      {done ? <>
        <div style={{ textAlign:"center", padding:"16px 0" }}>
          <div style={{ fontSize:40, marginBottom:12 }}>✓</div>
          <div style={{ fontFamily:"'Barlow Condensed', sans-serif", fontSize:22, fontWeight:800, color:"#3d9e7a", marginBottom:8 }}>PASSWORD RESET</div>
          <div style={{ fontSize:13, color:"#6b7280", marginBottom:20 }}>Your password has been updated.</div>
          <button onClick={onDone} style={{ width:"100%", padding:12, background:"#111827", color:"#fff", border:"none", borderRadius:8, fontFamily:"'Inter', sans-serif", fontSize:14, fontWeight:600, cursor:"pointer" }}>Go to Login</button>
        </div>
      </> : <>
        <div style={{ fontFamily:"'Barlow Condensed', sans-serif", fontSize:22, fontWeight:800, color:"#1a1a2e", marginBottom:4 }}>RESET PASSWORD</div>
        <div style={{ fontSize:13, color:"#6b7280", marginBottom:20, fontFamily:"'Inter', sans-serif" }}>Choose a new password</div>
        {error && <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:8, padding:"10px 14px", fontSize:13, color:"#dc2626", marginBottom:16 }}>{error}</div>}
        <label style={{ fontSize:11, fontWeight:600, color:"#6b7280", letterSpacing:"0.06em", marginBottom:5, display:"block", fontFamily:"'Inter', sans-serif" }}>NEW PASSWORD</label>
        <input type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="At least 6 characters" style={{ width:"100%", padding:"10px 14px", background:"#f9fafb", border:"1px solid #e5e7eb", borderRadius:8, fontFamily:"'Inter', sans-serif", fontSize:14, color:"#1a1a2e", marginBottom:12, outline:"none" }}/>
        <label style={{ fontSize:11, fontWeight:600, color:"#6b7280", letterSpacing:"0.06em", marginBottom:5, display:"block", fontFamily:"'Inter', sans-serif" }}>CONFIRM PASSWORD</label>
        <input type="password" value={confirm} onChange={e=>setConfirm(e.target.value)} onKeyDown={e=>e.key==="Enter"&&submit()} placeholder="Repeat your password" style={{ width:"100%", padding:"10px 14px", background:"#f9fafb", border:"1px solid #e5e7eb", borderRadius:8, fontFamily:"'Inter', sans-serif", fontSize:14, color:"#1a1a2e", marginBottom:12, outline:"none" }}/>
        <button onClick={submit} disabled={loading} style={{ width:"100%", padding:12, background:"#111827", color:"#fff", border:"none", borderRadius:8, fontFamily:"'Inter', sans-serif", fontSize:14, fontWeight:600, cursor:"pointer" }}>{loading ? "Resetting..." : "Reset Password"}</button>
      </>}
    </div>
  </div>;
}

// ─── APP ROOT ─────────────────────────────────────────────────────────────────
export default function App() {
  const [screen, setScreen] = useState("home");
  const [prevScreen, setPrevScreen] = useState("landing");
  const [signupIntent, setSignupIntent] = useState(null); // "request" or "join"
  const [adminLeagueIds, setAdminLeagueIds] = useState(() => { try { return JSON.parse(localStorage.getItem("scraps_admin_ids") || "[]"); } catch(e) { return []; } });
  const [session, setSession] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [leagues, setLeagues] = useState([]);
  const [account, setAccount] = useState(null);

  // Session persistence
  useEffect(() => {
    const saved = localStorage.getItem("scraps_session");
    const savedAccount = localStorage.getItem("scraps_account");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setSession(parsed);
        if (savedAccount) setAccount(JSON.parse(savedAccount));
        // Always restore to hub, not back into a league
        setScreen("hub");
      } catch(e) {
        localStorage.removeItem("scraps_session");
      }
    }
  }, []);

  const handleLoggedIn = (data) => {
    const acct = data.account;
    setAccount(acct);
    localStorage.setItem("scraps_token", data.token);
    localStorage.setItem("scraps_account", JSON.stringify(acct));
    if (data.admin_league_ids) {
      setAdminLeagueIds(data.admin_league_ids);
      localStorage.setItem("scraps_admin_ids", JSON.stringify(data.admin_league_ids));
    }

    if (data.newJoin) {
      const sess = { team: data.team, league: data.league, account: acct };
      setSession(sess);
      setLeagues([{ team: data.team, league: data.league }]);
      localStorage.setItem("scraps_session", JSON.stringify(sess));
      setShowWelcome(true);
      setScreen("welcome");
    } else {
      // Always go to hub regardless of how many leagues
      setLeagues(data.leagues || []);
      setScreen("hub");
    }
  };

  const handlePick = (picked) => {
    if (picked === "join") { setScreen("join-existing"); return; }
    setSession(picked);
    localStorage.setItem("scraps_session", JSON.stringify(picked));
    setScreen("app");
  };

  // Mark the current session's team as having seen the league rules pop-up,
  // both on the backend (so it stays dismissed across devices) and in the
  // local session state (so it doesn't flash again this visit).
  const markRulesSeen = () => {
    if (!session?.team?.id) return;
    fetch(`${API}/team/${session.team.id}/mark-rules-seen`, { method: "POST" }).catch(() => {});
    setSession(s => {
      if (!s) return s;
      const updated = { ...s, team: { ...s.team, rules_seen: true } };
      localStorage.setItem("scraps_session", JSON.stringify(updated));
      return updated;
    });
  };

  const handleLeave = () => {
    setSession(null);
    setScreen("landing");
    setShowWelcome(false);
    localStorage.removeItem("scraps_session");
    localStorage.removeItem("scraps_token");
    localStorage.removeItem("scraps_account");
  };

  // Handle email verification link
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const verifyToken = params.get("token");
    const resetToken = params.get("reset-password") || (window.location.pathname === "/reset-password" ? params.get("token") : null);

    if (verifyToken && window.location.pathname !== "/reset-password") {
      fetch(`${API}/auth/verify-email?token=${verifyToken}`, { method:"POST" })
        .then(r => r.json())
        .then(data => {
          alert(data.message === "Email verified successfully"
            ? "✓ Email verified! You can now log in."
            : data.detail || "Verification failed");
          window.history.replaceState({}, "", "/");
        })
        .catch(() => alert("Verification failed — please try again"));
    }
    if (window.location.pathname === "/reset-password") {
      const token = params.get("token");
      if (token) setScreen("reset-password-" + token);
    }
  }, []);

  if (screen === "home") return <HomePage
    onSignIn={() => setScreen("landing")}
    onJoin={() => { setSignupIntent("join"); setScreen("signup"); }}
    onRequest={() => { setSignupIntent("request"); setScreen("signup"); }}
    onCreateAccount={() => { setSignupIntent(null); setScreen("signup"); }}
  />;
  if (screen === "signup") return <SignupWall
    intent={signupIntent}
    onSignIn={() => setScreen("landing")}
    onBack={() => setScreen("home")}
    onCreated={(data) => {
      handleLoggedIn(data);
      if (signupIntent === "request") { setPrevScreen("hub"); setScreen("request"); }
      else if (signupIntent === "join") setScreen("join");
      else setScreen("hub");
    }}
  />;
  if (screen === "superadmin") return <SuperAdmin onBack={() => setScreen("landing")} />;
  if (screen === "terms") return <Terms onBack={() => setScreen("landing")} />;
  if (screen === "forgot") return <ForgotPasswordScreen onBack={() => setScreen("landing")} />;
  if (screen?.startsWith("reset-password-")) {
    const token = screen.replace("reset-password-", "");
    return <ResetPasswordScreen token={token} onDone={() => setScreen("landing")} />;
  }
  if (screen === "hub") return (
    <MyLeagues
      leagues={leagues}
      account={account}
      accountName={account?.full_name}
      onEnter={(data) => {
        const acct = account || JSON.parse(localStorage.getItem("scraps_account") || "null");
        const sess = {...data, account: acct};
        setSession(sess);
        setAccount(acct);
        localStorage.setItem("scraps_session", JSON.stringify(sess));
        const lt = data.league.league_type || "classic";
        setScreen(lt === "eliminator" ? "eliminator" : lt === "h2h" ? "h2h" : "app");
      }}
      onJoin={() => setScreen("join")}
      adminLeagueIds={adminLeagueIds}
      onAdminIdsLoaded={(ids) => { setAdminLeagueIds(ids); localStorage.setItem("scraps_admin_ids", JSON.stringify(ids)); }}
      onRequest={() => { setPrevScreen("hub"); setScreen("request"); }}
      onLogout={handleLeave}
    />
  );
  if (screen === "eliminator" && session) return (
    <>
      <Eliminator
        key={session.team.id}
        teamId={session.team.id}
        leagueId={session.league.id}
        teamName={session.team.team_name}
        leagueName={session.league.name}
        isAdmin={adminLeagueIds.includes(session?.league?.id)}
        onAdminClick={() => setScreen("leagueadmin-overlay")}
        token={localStorage.getItem("scraps_token")}
        onBack={() => { setScreen("hub"); }}
      />
      {!session.team.rules_seen && (
        <LeagueRulesModal leagueType="eliminator" onClose={markRulesSeen} />
      )}
    </>
  );
  if (screen === "h2h" && session) return (
    <>
      <H2HLeague
        key={session.team.id}
        isAdmin={adminLeagueIds.includes(session?.league?.id)}
        onAdminClick={() => setScreen("leagueadmin-overlay")}
        teamId={session.team.id}
        leagueId={session.league.id}
        teamName={session.team.team_name}
        leagueName={session.league.name}
        emailVerified={session.account?.email_verified ?? true}
        email={session.account?.email ?? ""}
        onBack={() => setScreen("hub")}
      />
      {!session.team.rules_seen && (
        <LeagueRulesModal leagueType="h2h" onClose={markRulesSeen} />
      )}
    </>
  );
  if (screen === "leagueadmin") return <LeagueAdmin onBack={() => setScreen("landing")} />;
  if (screen === "request") return <RequestForm onBack={() => setScreen(prevScreen === "hub" ? "hub" : "home")} account={account} />;
  if (screen === "join") return <JoinScreen
    account={account}
    token={session?.token || localStorage.getItem("scraps_token")}
    onBack={() => setScreen(localStorage.getItem("scraps_token") ? "hub" : "home")}
    onJoined={async (data) => {
      if (session) {
        // Already logged in - do a full refresh to get all leagues including new one
        const token = localStorage.getItem("scraps_token");
        try {
          const res = await fetch(`${API}/auth/login-refresh`, {
            method:"POST",
            headers:{"Content-Type":"application/json", Authorization:`Bearer ${token}`},
          });
          if (res.ok) {
            const refreshData = await res.json();
            handleLoggedIn({ ...refreshData, token });
          } else {
            handleLoggedIn({ ...data, token, account: account });
          }
        } catch(e) {
          handleLoggedIn({ ...data, token, account: account });
        }
      } else {
        handleLoggedIn({ token:data.token, account:data.account, leagues:[{team:data.team, league:data.league}], newJoin:true, team:data.team, league:data.league });
      }
    }}
  />;
  if (screen === "picker") return <LeaguePicker leagues={leagues} onPick={handlePick} accountName={account?.full_name} />;
  if (screen === "welcome" && showWelcome && session) return (
    <WelcomeScreen session={session} onContinue={() => {
      setShowWelcome(false);
      const lt2 = session.league?.league_type || "classic";
      setScreen(lt2 === "eliminator" ? "eliminator" : "app");
    }} />
  );
  if (screen === "leagueadmin-overlay") return (
    <LeagueAdminOverlay
      leagueId={session?.league?.id}
      leagueType={session?.league?.league_type}
      token={localStorage.getItem("scraps_token")}
      league={session?.league}
      onBack={() => setScreen(session?.league?.league_type === "h2h" ? "h2h" : session?.league?.league_type === "eliminator" ? "eliminator" : "app")}
    />
  );

  if (screen === "app" && session) return (
    <>
      <Sportsbook
        key={session.team.id}
        isAdmin={adminLeagueIds.includes(session?.league?.id)}
        onAdminClick={() => setScreen("leagueadmin-overlay")}
        teamId={session.team.id}
        leagueId={session.league.id}
        teamName={session.team.team_name}
        credits={session.team.credits}
        onLeaveLeague={() => setScreen("hub")}
        leagueName={session.league.name}
        enabledSports={session.league.sports}
        playerProps={session.league.player_props}
        emailVerified={session.account?.email_verified ?? true}
        email={session.account?.email ?? ""}
      />
      {!session.team.rules_seen && (
        <LeagueRulesModal leagueType="classic" onClose={markRulesSeen} />
      )}
    </>
  );

  return (
    <Landing
      onLoggedIn={handleLoggedIn}
      onSuperAdmin={() => setScreen("superadmin")}
      onLeagueAdmin={() => setScreen("leagueadmin")}
      onJoin={() => setScreen("join")}
      onRequest={() => { setPrevScreen("landing"); setScreen("request"); }}
      onForgot={() => setScreen("forgot")}
      onTerms={() => setScreen("terms")}
      onHome={() => setScreen("home")}
    />
  );
}

// ─── JOIN SCREEN (new users) ──────────────────────────────────────────────────
function JoinScreen({ onBack, onJoined, account, token }) {
  const [step, setStep] = useState("invite");
  const [inviteCode, setInviteCode] = useState("");
  const [leagueInfo, setLeagueInfo] = useState(null);
  const [teamName, setTeamName] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const isLoggedIn = !!(account?.email || account?.full_name);

  const checkInvite = async () => {
    if (!inviteCode) { setError("Please enter an invite code"); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/league/invite/${inviteCode.toUpperCase().trim()}/validate`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      setLeagueInfo(data);
      setStep("details");
    } catch(e) { setError(e.message); }
    setLoading(false);
  };

  const joinLeague = async () => {
    if (!teamName) { setError("Please enter a team name"); return; }
    if (!isLoggedIn && (!fullName || !email || !password)) { setError("Please fill in all fields"); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/auth/register`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({
          email: isLoggedIn ? account.email : email,
          password: isLoggedIn ? "__use_existing_account__" : password,
          full_name: isLoggedIn ? (account.full_name || account.name) : fullName,
          invite_code: inviteCode.toUpperCase().trim(),
          team_name: teamName,
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      onJoined(data);
    } catch(e) { setError(e.message); }
    setLoading(false);
  };

  return <div style={S.wrap}>
    <style>{FONT}</style>
    <Logo/>
    <div style={S.card}>
      {step === "invite" && <>
        <div style={{fontFamily:"'Barlow Condensed', sans-serif", fontSize:22, fontWeight:800, marginBottom:4}}>JOIN A LEAGUE</div>
        <div style={{fontSize:13, color:"#8a96b0", marginBottom:20}}>Enter the invite code your league admin shared with you</div>
        {error && <div style={S.err}>{error}</div>}
        <label style={S.label}>INVITE CODE</label>
        <input value={inviteCode} onChange={e=>setInviteCode(e.target.value)} onKeyDown={e=>e.key==="Enter"&&checkInvite()} placeholder="e.g. X7K2PQ" style={{...S.input, textTransform:"uppercase", letterSpacing:"0.14em", fontSize:22, fontFamily:"'Barlow Condensed', sans-serif", fontWeight:800, textAlign:"center", boxSizing:"border-box"}}/>
        <button onClick={checkInvite} disabled={loading} style={S.btnGreen}>{loading?"CHECKING...":"NEXT →"}</button>
        <button onClick={onBack} style={S.btnGhost}>← Back</button>
      </>}

      {step === "details" && <>
        <div style={{fontFamily:"'Barlow Condensed', sans-serif", fontSize:22, fontWeight:800, marginBottom:4, color:"#1a1a2e"}}>
          {leagueInfo?.league_name || "JOIN LEAGUE"}
        </div>
        {isLoggedIn && (
          <div style={{backgroundColor:"#f0fdf8", border:"1px solid #a7f3d0", borderRadius:8, padding:"10px 14px", marginBottom:16, fontSize:13, color:"#065f46"}}>
            ✓ Joining as <strong>{account.full_name || account.name}</strong> ({account.email})
          </div>
        )}
        {error && <div style={S.err}>{error}</div>}
        {!isLoggedIn && <>
          <label style={S.label}>FULL NAME</label>
          <input value={fullName} onChange={e=>setFullName(e.target.value)} placeholder="Your real name" style={S.input}/>
          <label style={S.label}>EMAIL</label>
          <input value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@email.com" style={S.input}/>
          <label style={S.label}>PASSWORD</label>
          <input type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="Choose a strong password" style={S.input}/>
        </>}
        <label style={S.label}>TEAM NAME</label>
        <input value={teamName} onChange={e=>setTeamName(e.target.value)} placeholder="Pick something creative" style={S.input}/>
        <button onClick={joinLeague} disabled={loading} style={S.btnGreen}>
          {loading ? "JOINING..." : isLoggedIn ? "JOIN LEAGUE" : "CREATE ACCOUNT & JOIN"}
        </button>
        <button onClick={()=>{setStep("invite");setError("");}} style={S.btnGhost}>← Back</button>
      </>}
    </div>
  </div>;
}
